// @ts-nocheck
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Camera, Upload, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import heic2any from 'heic2any';
import { getSessionUser } from '@/lib/authSession';

const AI_FN = 'rle-ai-score-entry';
const SCORE_FN = 'rle-score-game';
const SCHEDULE_FN = 'rle-public-schedule';

interface Props {
  eventId: string;
  open: boolean;
  onClose: () => void;
}

const norm = (s: string) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// Token-overlap similarity 0..1
function similarity(a: string, b: string): number {
  const at = new Set(norm(a).split(' ').filter(Boolean));
  const bt = new Set(norm(b).split(' ').filter(Boolean));
  if (!at.size || !bt.size) return 0;
  let shared = 0;
  at.forEach((t) => bt.has(t) && shared++);
  return shared / Math.max(at.size, bt.size);
}

const isHeic = (file: File) =>
  /image\/(heic|heif)/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);

// Convert any image File to a downscaled JPEG base64 (longest side <= 1600px).
async function fileToJpegBase64(file: File): Promise<string> {
  let blob: Blob = file;
  if (isHeic(file)) {
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    blob = Array.isArray(converted) ? converted[0] : converted;
  }

  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });

  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process image.');
  ctx.drawImage(img, 0, 0, w, h);
  const jpegUrl = canvas.toDataURL('image/jpeg', 0.9);
  return jpegUrl.split(',')[1] || '';
}

type Step = 'capture' | 'reading' | 'review';

const AiScoreEntryDrawer: React.FC<Props> = ({ eventId, open, onClose }) => {
  const [step, setStep] = useState<Step>('capture');
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [scoreA, setScoreA] = useState('');
  const [scoreB, setScoreB] = useState('');
  const [confidence, setConfidence] = useState<string>('');
  const [games, setGames] = useState<any[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string>('');
  const [swapped, setSwapped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadBlob, setUploadBlob] = useState<Blob | null>(null);
  const [uploadExt, setUploadExt] = useState<string>('jpg');

  const reset = () => {
    setStep('capture');
    setPreview(null);
    setResult(null);
    setTeamA('');
    setTeamB('');
    setScoreA('');
    setScoreB('');
    setConfidence('');
    setSelectedGameId('');
    setSwapped(false);
    setUploadBlob(null);
    setUploadExt('jpg');
  };

  useEffect(() => {
    if (open) reset();
  }, [open]);

  // Load this event's games (current published schedule).
  useEffect(() => {
    if (!open || !eventId) return;
    (async () => {
      const { data: ev } = await supabase
        .from('events')
        .select('slug')
        .eq('id', eventId)
        .maybeSingle();
      if (!ev?.slug) return;
      const { data: res } = await supabase.functions.invoke(SCHEDULE_FN, {
        body: { action: 'get', slug: ev.slug },
      });
      if (res?.ok) {
        const flat: any[] = [];
        (res.data?.days || []).forEach((d: any) =>
          (d.games || []).forEach((g: any) => flat.push(g)),
        );
        setGames(flat);
      }
    })();
  }, [open, eventId]);

  const openGames = useMemo(
    () => games.filter((g) => (g.status || g.game_status) !== 'completed'),
    [games],
  );

  // Best match against open games using both teams.
  const autoMatch = useCallback(
    (aName: string, bName: string) => {
      let best: any = null;
      let bestScore = 0;
      let bestSwap = false;
      for (const g of openGames) {

        const direct = similarity(aName, g.team_a) + similarity(bName, g.team_b);
        const swap = similarity(aName, g.team_b) + similarity(bName, g.team_a);
        const s = Math.max(direct, swap);
        if (s > bestScore) {
          bestScore = s;
          best = g;
          bestSwap = swap > direct;
        }
      }
      return { best, bestScore, bestSwap };
    },
    [openGames],
  );

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    setStep('reading');

    const isPdf = /pdf/i.test(file.type) || /\.pdf$/i.test(file.name);
    try {
      // Keep the original upload (converting HEIC to JPEG) to persist as the record.
      if (isPdf) {
        setUploadBlob(file);
        setUploadExt('pdf');
      } else if (isHeic(file)) {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
        setUploadBlob(Array.isArray(converted) ? converted[0] : converted);
        setUploadExt('jpg');
      } else {
        setUploadBlob(file);
        setUploadExt(/png/i.test(file.type) || /\.png$/i.test(file.name) ? 'png' : 'jpg');
      }

      if (isPdf) {
        // PDFs are stored as the record but can't be auto-read; staff fill in manually.
        setResult({});
        toast.message('PDF uploaded. Enter the score manually below.');
        setStep('review');
        return;
      }

      const base64 = await fileToJpegBase64(file);
      const { data, error } = await invokeEdgeFunction(AI_FN, {
        body: { image_base64: base64, media_type: 'image/jpeg' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const r = data?.result;
      if (!r) throw new Error('Could not read the score sheet. Try a clearer photo.');
      setResult(r);
      setTeamA(r.team_a_name || '');
      setTeamB(r.team_b_name || '');
      setScoreA(r.score_a == null ? '' : String(r.score_a));
      setScoreB(r.score_b == null ? '' : String(r.score_b));
      setConfidence(r.confidence || '');

      const { best, bestScore, bestSwap } = autoMatch(r.team_a_name || '', r.team_b_name || '');
      if (best && bestScore >= 0.5) {
        setSelectedGameId(best.id);
        setSwapped(bestSwap);
      }
      setStep('review');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to read score sheet.');
      setStep('capture');
    }
  };

  const selectedGame = useMemo(
    () => games.find((g) => g.id === selectedGameId) || null,
    [games, selectedGameId],
  );

  const confidenceColor =
    confidence === 'high' ? 'default' : confidence === 'medium' ? 'secondary' : 'destructive';

  const handleSave = async () => {
    if (!selectedGame) {
      toast.error('Pick the game to apply this score to.');
      return;
    }
    const a = parseInt(scoreA, 10);
    const b = parseInt(scoreB, 10);
    if (Number.isNaN(a) || Number.isNaN(b)) {
      toast.error('Enter both scores.');
      return;
    }
    // Map AI team_a/team_b onto the game's team_a/team_b sides.
    const gameScoreA = swapped ? b : a;
    const gameScoreB = swapped ? a : b;
    setSaving(true);
    try {
      const { data, error } = await invokeEdgeFunction(SCORE_FN, {
        body: {
          action: 'complete',
          game_id: selectedGame.id,
          score_a: gameScoreA,
          score_b: gameScoreB,
        },
      });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data?.error?.message || 'Save failed.');

      // Persist the uploaded score sheet image as the durable record (non-blocking).
      if (uploadBlob) {
        try {
          const contentType =
            uploadExt === 'pdf'
              ? 'application/pdf'
              : uploadExt === 'png'
                ? 'image/png'
                : 'image/jpeg';
          const path = `${eventId}/${selectedGame.id}-${Date.now()}.${uploadExt}`;
          const { error: upErr } = await supabase.storage
            .from('score-sheets')
            .upload(path, uploadBlob, { contentType, upsert: true });
          if (upErr) throw upErr;
          const { data: pub } = supabase.storage.from('score-sheets').getPublicUrl(path);
          const { data: userData } = await getSessionUser();
          await supabase
            .from('scheduled_games')
            .update({
              score_sheet_image_url: pub?.publicUrl || null,
              score_sheet_uploaded_at: new Date().toISOString(),
              score_sheet_uploaded_by: userData?.user?.id || null,
            })
            .eq('id', selectedGame.id);
        } catch (upErr: any) {
          toast.warning('Score saved, but the score sheet image could not be stored.');
        }
      }

      toast.success('Score saved. Game marked completed.');
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save score.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>AI Score Entry</SheetTitle>
        </SheetHeader>

        {step === 'capture' && (
          <div className="py-8 space-y-4">
            <p className="text-sm text-muted-foreground">
              Photograph the completed score sheet. The AI reads the final score; you confirm before it saves.
            </p>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/40">
              <Camera className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">Take / upload photo</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          </div>
        )}

        {step === 'reading' && (
          <div className="py-16 flex flex-col items-center gap-3 text-muted-foreground">
            {preview && (
              <img src={preview} alt="score sheet" className="max-h-40 rounded border mb-2" />
            )}
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-sm">Converting / reading…</p>
          </div>
        )}

        {step === 'review' && (
          <div className="py-6 space-y-5">
            {preview && (
              <img src={preview} alt="score sheet" className="max-h-36 rounded border mx-auto" />
            )}
            {confidence && (
              <div className="flex items-center gap-2 text-sm">
                <span>AI confidence:</span>
                <Badge variant={confidenceColor as any}>{confidence}</Badge>
              </div>
            )}

            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Team A</Label>
                <Input value={teamA} onChange={(e) => setTeamA(e.target.value)} />
              </div>
              <div className="space-y-1 w-20">
                <Label className="text-xs">Score</Label>
                <Input
                  inputMode="numeric"
                  value={scoreA}
                  onChange={(e) => setScoreA(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Team B</Label>
                <Input value={teamB} onChange={(e) => setTeamB(e.target.value)} />
              </div>
              <div className="space-y-1 w-20">
                <Label className="text-xs">Score</Label>
                <Input
                  inputMode="numeric"
                  value={scoreB}
                  onChange={(e) => setScoreB(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Apply to game</Label>
              <Select value={selectedGameId} onValueChange={setSelectedGameId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a game…" />
                </SelectTrigger>
                <SelectContent>
                  {openGames.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.team_a} vs {g.team_b}
                      {g.court ? ` · ${g.court}` : ''}
                      {g.start ? ` · ${g.start}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedGame && (
              <div className="rounded-md border p-3 text-sm space-y-2 bg-muted/30">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {selectedGame.team_a} vs {selectedGame.team_b}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setSwapped((s) => !s)}
                  >
                    <ArrowLeftRight className="h-3 w-3 mr-1" /> Swap sides
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  Will save: <strong>{selectedGame.team_a}</strong> {swapped ? scoreB : scoreA} —{' '}
                  {swapped ? scoreA : scoreB} <strong>{selectedGame.team_b}</strong>
                </div>
                {(selectedGame.court || selectedGame.start) && (
                  <div className="text-xs text-muted-foreground">
                    {[selectedGame.court, selectedGame.start].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep('capture')} disabled={saving}>
                Retake
              </Button>
              <Button className="flex-1" onClick={handleSave} disabled={saving || !selectedGameId}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Confirm & Save
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AiScoreEntryDrawer;
